/**
 * Licenses Hooks - TanStack Query implementation
 *
 * Server state management for license data.
 * Follows MaldevtaHub architectural principles.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { queryKeys, invalidateQueries } from '@/lib/query-keys'
import { toast } from 'sonner'
import type { CreateLicenseData, License } from '@/lib/types'

// Types
export interface LicenseParams {
  plan?: string
  valid?: boolean
}

export interface UpdateLicenseData {
  expiresAt?: string
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch all licenses with optional filters
 */
export function useLicenses(params: LicenseParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.licenses.lists(), params] as const,
    queryFn: async () => {
      const response = await api.listLicenses(params)
      // Check if response has data property (standard API wrapper)
      return response.data || []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Fetch license detail by ID
 */
export function useLicense(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.licenses.detail(id) : ['license', 'null'],
    queryFn: async () => {
      if (!id) return null
      const response = await api.getLicense(id)
      return response.data || null
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create new license
 */
export function useCreateLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateLicenseData) => {
      const res = await api.createLicense(data)
      console.log('DEBUG createLicense result:', res)
      if (!res.success) {
        const error = new Error(res.error || 'Failed to create license')
        // Attach additional error details (like fieldErrors) to the error object
        Object.assign(error, res)
        throw error
      }
      return res
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'licenses')
      toast.success('License created successfully')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create license')
    },
  })
}

/**
 * Update license expiration
 */
export function useUpdateLicenseExpiration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: number; expiresAt: string }) =>
      api.updateLicenseExpiration(id, expiresAt),

    onMutate: async ({ id, expiresAt }) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.licenses.lists() })

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData(queryKeys.licenses.lists())

      // Optimistic update
      queryClient.setQueryData(
        queryKeys.licenses.lists(),
        (old: License[] | undefined) =>
          old?.map(lic =>
            lic.id === id ? { ...lic, expiresAt } : lic
          )
      )

      return { previous }
    },

    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.licenses.lists(), context.previous)
      }
      toast.error(err?.message || 'Failed to update license')
    },

    onSettled: () => {
      // Always invalidate after mutation
      invalidateQueries(queryClient, 'licenses')
    },
  })
}

/**
 * Revoke/delete license
 */
export function useRevokeLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.revokeLicense(id),

    onSuccess: () => {
      invalidateQueries(queryClient, 'licenses')
      toast.success('License revoked')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to revoke license')
    },
  })
}

/**
 * Validate license key
 */
export function useValidateLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (key: string) => api.validateLicense(key),

    onSuccess: () => {
      // Invalidate license queries on validation
      invalidateQueries(queryClient, 'licenses')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'License validation failed')
    },
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to refetch all license queries
 */
export function useRefetchLicenses() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.licenses.root,
    })
  }
}
